import type { CoreClient } from '@ingenico/api-sdk-core/common';
import type {
  EnumScreenShareStatusApi,
  ScreenShareDeviceAnswerResourceReadApi,
  ScreenShareDeviceIceResourceReadApi,
  ScreenShareDeviceOfferResourceReadApi,
  ScreenShareStatusResourceReadApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  createCloseScreenShareSessionV2,
  createOpenScreenShareSessionV2,
  createScreenShareBrowserAnswerV2,
  createScreenShareBrowserIceV2,
  createScreenShareDeviceOfferV2,
  enumScreenShareStatus,
  getScreenShareConfigurationV2,
  getScreenShareDeviceAnswerV2,
  getScreenShareDeviceIceV2,
  getScreenShareDeviceOfferV2,
  getScreenShareStatusV2,
  updateScreenShareStatusToConnectedV2,
} from '@ingenico/api-sdk-device-management-private-v2';
import { CurrentSectionSpiIt } from '@ingenico/react-section-utils';
import { isNonNullable } from '@ingenico/util-common';
import { inject, injectable } from 'inversify';
import type { Subscription } from 'rxjs';
import {
  animationFrames,
  BehaviorSubject,
  catchError,
  combineLatest,
  delay,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  forkJoin,
  from,
  fromEvent,
  last,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  scan,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  throwError,
  timer,
} from 'rxjs';
import type { DeviceManagementSpiPort } from '../../hexa/ports/spi.model';
import type {
  DeviceFrameRatio,
  RemoteFrameSize,
  ScreenShareEvent,
  ScreenShareInitStatus,
} from '../../models/screenShare.model';
import { ScreenShareButtonTrigger, ScreenShareSessionStatus } from '../../models/screenShare.model';
import { ScreenShareUtil } from '../utils/screen-share.util';

@injectable()
export class ScreenShareService {
  private static readonly SCREEN_SHARE_DELAY = 1000;
  private static readonly SCREEN_SHARE_DEVICE_ANSWER_INTERVAL = 1000;
  private static readonly SCREEN_SHARE_DEVICE_ICE_CANDIDATE_INTERVAL = 1000;
  private static readonly SCREEN_SHARE_DEVICE_OFFER_INTERVAL = 1000;
  private static readonly SCREEN_SHARE_STATUS_CHECK_INTERVAL = 1000;
  private static readonly WAIT_DEVICE_ANSWER_TIMEOUT = 2 * 60 * 1000;
  private static readonly WAIT_DEVICE_ICE_CANDIDATE_TIMEOUT = 2 * 60 * 1000;
  private static readonly WAIT_DEVICE_OFFER_TIMEOUT = 2 * 60 * 1000;
  private static readonly WAIT_FOR_STATUS_TIMEOUT = 2 * 60 * 1000;
  private readonly actionSubject = new Subject<ScreenShareButtonTrigger>();
  private readonly closeSubject = new Subject<void>();
  private readonly eventSubject = new Subject<ScreenShareEvent>();
  private readonly sessionStatusSubject = new BehaviorSubject<ScreenShareSessionStatus>(
    ScreenShareSessionStatus.DISCONNECTED,
  );
  private readonly apiSdkClient: CoreClient;
  private readonly connectionRefusedByUser = new Subject<void>();

  constructor(@inject(CurrentSectionSpiIt) spi: DeviceManagementSpiPort) {
    this.apiSdkClient = spi.getApiSdkClient();
  }

  closeScreenShare(): void {
    this.closeSubject.next(void 0);
  }

  getEvents(): Observable<ScreenShareEvent> {
    return this.eventSubject.asObservable();
  }

  getInitStatus(): Observable<ScreenShareInitStatus> {
    return of(void 0).pipe(
      mergeMap(() => this.getScreenShareSessionStatus()),
      filter((status) => status === ScreenShareSessionStatus.DISCONNECTED),
      switchMap(() => this.computeInitStatus()),
    );
  }

  getScreenShareSessionStatus(): Observable<ScreenShareSessionStatus> {
    return this.sessionStatusSubject.asObservable();
  }

  getCurrentScreenShareSessionStatus(): ScreenShareSessionStatus {
    return this.sessionStatusSubject.getValue();
  }

  private openSessionIfNeeded(deviceId: string): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() => {
        const statusVal = this.sessionStatusSubject.getValue();
        return [ScreenShareSessionStatus.RefusedByCustomer, ScreenShareSessionStatus.RECONNECTING].includes(statusVal)
          ? this.openScreenShareSession(deviceId)
          : of(void 0);
      }),
    );
  }

  private getInitialLaunchStatus(): ScreenShareSessionStatus {
    return this.sessionStatusSubject.getValue() === ScreenShareSessionStatus.DISCONNECTED
      ? ScreenShareSessionStatus.DISCONNECTED
      : ScreenShareSessionStatus.RECONNECTING;
  }

  launchScreenShare(containerEl: HTMLElement, deviceId: string): Observable<void> {
    return of(void 0)
      .pipe(
        tap(() => this.sessionStatusSubject.next(this.getInitialLaunchStatus())),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_START' })),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_01_CLOSE_SESSION_START' })),
        mergeMap(() =>
          this.sessionStatusSubject.getValue() === ScreenShareSessionStatus.RECONNECTING
            ? of(void 0)
            : this.closeSessionIfNeeded(deviceId),
        ),
        mergeMap(() => this.openSessionIfNeeded(deviceId)),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_01_CLOSE_SESSION_END' })),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_02_WAIT_SESSION_READY_TO_BE_OPEN_START' })),
        mergeMap(() =>
          this.waitForScreenShareStatus(deviceId, [
            enumScreenShareStatus.CLOSED,
            enumScreenShareStatus.FAILED,
            enumScreenShareStatus.REFUSED_BY_CUSTOMER,
          ]),
        ),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_02_WAIT_SESSION_READY_TO_BE_OPEN_END' })),
      )
      .pipe(
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_03_OPEN_SESSION_START' })),
        mergeMap(() => this.openScreenShareSession(deviceId)),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_03_OPEN_SESSION_END' })),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_04_WAIT_DEVICE_READY_FOR_SIGNALING_START' })),
        mergeMap(() =>
          this.waitForScreenShareStatus(deviceId, [
            enumScreenShareStatus.READY_FOR_SIGNALING,
            enumScreenShareStatus.REFUSED_BY_CUSTOMER,
          ]),
        ),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_04_WAIT_DEVICE_READY_FOR_SIGNALING_END' })),
      )
      .pipe(
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_05_GET_RTC_CONFIGURATION_START' })),
        mergeMap(() => this.getRTCConfiguration()),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_05_GET_RTC_CONFIGURATION_END' })),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_06_INIT_RTC_START' })),
        mergeMap((configuration) => this.getRtcPeerConnectionAndDataChannel(configuration)),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_06_INIT_RTC_END' })),
      )
      .pipe(
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_07_CONNECT_RTC_SESSION_START' })),
        mergeMap(([peerConnection, dataChannel]) =>
          this.manageRTCAndStreaming(deviceId, peerConnection, containerEl, dataChannel),
        ),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_07_CONNECT_RTC_SESSION_END' })),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'G_INIT_END' })),
        tap(() => this.sessionStatusSubject.next(ScreenShareSessionStatus.CONNECTED)),
        finalize(() =>
          this.sessionStatusSubject.getValue() === ScreenShareSessionStatus.RefusedByCustomer
            ? void 0
            : this.finallyCloseSession(deviceId),
        ),
        takeUntil(this.stopSignalGlobal()),
        takeUntil(this.connectionRefusedByUser),
      );
  }

  triggerAppsButton(): void {
    this.actionSubject.next(ScreenShareButtonTrigger.APPS);
  }

  triggerBackButton(): void {
    this.actionSubject.next(ScreenShareButtonTrigger.BACK);
  }

  triggerHomeButton(): void {
    this.actionSubject.next(ScreenShareButtonTrigger.HOME);
  }

  private askDeviceOffer(deviceId: string): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'CREATE_DEVICE_OFFER_START' })),
      mergeMap(() => this.apiSdkClient.request(createScreenShareDeviceOfferV2(deviceId, {}))),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'CREATE_DEVICE_OFFER_SUCCESS' })),
      catchError((error) => this.rethrowError('CREATE_DEVICE_OFFER_ERROR', error)),
    );
  }

  private closeSessionIfNeeded(deviceId: string): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'CLOSE_DEVICE_SESSION_START' })),
      mergeMap(() => this.getScreenShareStatus(deviceId)),
      mergeMap(({ status }) =>
        (
          [
            enumScreenShareStatus.CLOSED,
            enumScreenShareStatus.CLOSING,
            enumScreenShareStatus.REFUSED_BY_CUSTOMER,
          ] as EnumScreenShareStatusApi[]
        ).includes(status)
          ? of(void 0)
          : this.apiSdkClient.request(createCloseScreenShareSessionV2(deviceId, {})),
      ),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'CLOSE_DEVICE_SESSION_SUCCESS' })),
      catchError((error) => this.rethrowError('CLOSE_DEVICE_SESSION_ERROR', error)),
    );
  }

  private computeInitStatus(): Observable<ScreenShareInitStatus> {
    const STEPS_NUMBER = 9;
    return this.eventSubject.asObservable().pipe(
      filter(({ code, type }) => code.startsWith('G_INIT') || type === 'RESET'),
      scan<ScreenShareEvent, ScreenShareInitStatus>(
        (acc, { code, type }) => {
          const calc = code.endsWith('END') ? acc.progressInPercent + 100 / STEPS_NUMBER : acc.progressInPercent;
          const progressInPercent = type === 'RESET' ? 0 : calc;
          return {
            progressInPercent,
            message: code as ScreenShareInitStatus['message'],
          };
        },
        {
          progressInPercent: 100 / STEPS_NUMBER,
          message: 'G_INIT_START',
        },
      ),
      startWith({
        progressInPercent: 0,
        message: 'G_INIT_START',
      } as ScreenShareInitStatus),
    );
  }

  private errorAfter(ms: number, code: ScreenShareEvent['code']): Observable<void> {
    return timer(ms).pipe(
      mergeMap(() => this.rethrowError(code)),
      startWith(void 0),
    );
  }

  private manageFinalStreamStatus(currentStatus: ScreenShareSessionStatus): ScreenShareSessionStatus {
    return currentStatus === ScreenShareSessionStatus.ERROR
      ? ScreenShareSessionStatus.ERROR
      : ScreenShareSessionStatus.DISCONNECTED;
  }

  private finallyCloseSession(deviceId: string): Subscription {
    const currentStatus = this.sessionStatusSubject.getValue();
    const isReconnecting = currentStatus === ScreenShareSessionStatus.RECONNECTING;

    const newStatus = isReconnecting
      ? ScreenShareSessionStatus.RECONNECTING
      : this.manageFinalStreamStatus(currentStatus);

    this.sessionStatusSubject.next(newStatus);
    return isReconnecting
      ? of(void 0).subscribe()
      : this.apiSdkClient
          .request(createCloseScreenShareSessionV2(deviceId, {}))
          .pipe(catchError(() => EMPTY))
          .subscribe();
  }

  private getAllBrowserICECandidates(peerConnection: RTCPeerConnection): Observable<RTCIceCandidate[]> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_ALL_BROWSER_ICE_CANDIDATE_START' })),
      mergeMap(() => ScreenShareUtil.onIceCandidateUnSafe(peerConnection)),
      map(({ candidate }) => candidate),
      takeWhile((candidate) => candidate !== null),
      scan((acc, value) => [...acc, value], [] as RTCIceCandidate[]),
      last(),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_ALL_BROWSER_ICE_CANDIDATE_SUCCESS' })),
    );
  }

  private getRTCConfiguration(): Observable<Record<string, unknown>> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_CONFIGURATION_START' })),
      mergeMap(() => this.apiSdkClient.request(getScreenShareConfigurationV2())),
      catchError((error) => this.rethrowError('GET_CONFIGURATION_ERROR', error)),
      mergeMap(({ configuration }) => this.parseConfiguration(configuration)),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_CONFIGURATION_SUCCESS' })),
    );
  }

  private getRemoteStream(peerConnection: RTCPeerConnection): Observable<MediaStream> {
    const remoteStream = new MediaStream();
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_REMOTE_STREAM_START' })),
      mergeMap(() => ScreenShareUtil.onTrackSafe(peerConnection)),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_REMOTE_STREAM_TRACK' })),
      tap((track) => remoteStream.addTrack(track)),
      mergeMap(() => EMPTY),
      startWith(remoteStream),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_REMOTE_STREAM_SUCCESS' })),
    );
  }

  private getRtcPeerConnectionAndDataChannel(
    configuration: Record<string, unknown>,
  ): Observable<[RTCPeerConnection, RTCDataChannel]> {
    try {
      const peerConnection = new RTCPeerConnection(configuration);
      const dataChannel = peerConnection.createDataChannel('cmd');
      return of([peerConnection, dataChannel]);
    } catch (error) {
      return this.rethrowError('RTC_CONNECTION_ERROR', error);
    }
  }

  private getScreenShareDeviceAnswer(deviceId: string): Observable<ScreenShareDeviceAnswerResourceReadApi> {
    return of(void 0).pipe(
      delay(ScreenShareService.SCREEN_SHARE_DELAY),
      mergeMap(() => this.apiSdkClient.request(getScreenShareDeviceAnswerV2(deviceId))),
      catchError((error: { status: number } | null) =>
        error?.status === 404 ? of({ answer: '' }) : throwError(() => error),
      ),
    );
  }

  private getScreenShareDeviceIce(deviceId: string): Observable<ScreenShareDeviceIceResourceReadApi> {
    return of(void 0).pipe(
      delay(ScreenShareService.SCREEN_SHARE_DELAY),
      mergeMap(() => this.apiSdkClient.request(getScreenShareDeviceIceV2(deviceId))),
      catchError((error: { status: number } | null) =>
        error?.status === 404 ? of({ ice: '' }) : throwError(() => error),
      ),
    );
  }

  private getScreenShareDeviceOffer(deviceId: string): Observable<ScreenShareDeviceOfferResourceReadApi> {
    return of(void 0).pipe(
      delay(ScreenShareService.SCREEN_SHARE_DELAY),
      mergeMap(() => this.apiSdkClient.request(getScreenShareDeviceOfferV2(deviceId))),
      catchError((error: { status: number } | null) =>
        error?.status === 404 ? of({ offer: '' }) : throwError(() => error),
      ),
    );
  }

  private getScreenShareStatus(deviceId: string): Observable<ScreenShareStatusResourceReadApi> {
    return of(void 0).pipe(
      delay(ScreenShareService.SCREEN_SHARE_DELAY),
      mergeMap(() => this.apiSdkClient.request(getScreenShareStatusV2(deviceId))),
      catchError((error: { status: number } | null) =>
        error?.status === 404
          ? of({ status: enumScreenShareStatus.CLOSED, statusDetails: '' })
          : throwError(() => error),
      ),
    );
  }

  private initiateRTCConnectivity(deviceId: string, peerConnection: RTCPeerConnection): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() =>
        forkJoin([
          this.manageBrowserICECandidate(deviceId, peerConnection),
          this.manageDeviceICECandidate(deviceId, peerConnection),
        ]),
      ),
      map(() => void 0),
    );
  }

  private initiateRTCPeer(deviceId: string, peerConnection: RTCPeerConnection): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'INITIATE_RTC_PEER_START' })),
      mergeMap(() =>
        forkJoin([
          this.initiateRTCSession(deviceId, peerConnection),
          this.initiateRTCConnectivity(deviceId, peerConnection),
        ]),
      ),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'INITIATE_RTC_PEER_SUCCESS' })),
      mergeMap(() => ScreenShareUtil.onSessionAndConnectivityConnectedSafe(peerConnection)),
      mergeMap(() =>
        this.apiSdkClient.request(updateScreenShareStatusToConnectedV2(deviceId, {})).pipe(catchError(() => EMPTY)),
      ),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'INITIATE_RTC_PEER_SUCCESS_AND_CONNECTED' })),
      map(() => void 0),
      takeUntil(
        this.waitForScreenShareStatus(deviceId, [enumScreenShareStatus.REFUSED_BY_CUSTOMER]).pipe(
          mergeMap(() => this.rethrowError('SCREEN_SHARE_SESSION_REFUSED_BY_USER')),
        ),
      ),
    );
  }

  private initiateRTCSession(deviceId: string, peerConnection: RTCPeerConnection): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() => this.askDeviceOffer(deviceId)),
      mergeMap(() => this.waitForDeviceOffer(deviceId)),
      mergeMap((offer) => this.registerDeviceOffer(peerConnection, offer)),
      mergeMap(() => from(peerConnection.createAnswer())),
      mergeMap((browserAnswer) => this.sendAndRegisterBrowserAnswer(deviceId, browserAnswer, peerConnection)),
    );
  }

  private listenButtonTrigger(dataChannel: RTCDataChannel): Observable<ScreenShareButtonTrigger> {
    return this.actionSubject.asObservable().pipe(tap((message) => ScreenShareUtil.sendMessage(dataChannel, message)));
  }

  private manageBrowserICECandidate(deviceId: string, peerConnection: RTCPeerConnection): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() => this.getAllBrowserICECandidates(peerConnection)),
      mergeMap((candidates) => this.sendBrowserICECandidates(deviceId, candidates)),
      map(() => void 0),
    );
  }

  private manageDeviceICECandidate(deviceId: string, peerConnection: RTCPeerConnection): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() =>
        forkJoin([this.waitForAllDeviceICECandidates(deviceId), this.onRemoteDescriptionSet(peerConnection)]),
      ),
      mergeMap(([candidates]) =>
        forkJoin(candidates.map((candidate) => from(peerConnection.addIceCandidate(candidate)))),
      ),
      map(() => void 0),
    );
  }

  private manageRTCAndStreaming(
    deviceId: string,
    peerConnection: RTCPeerConnection,
    containerEl: HTMLElement,
    dataChannel: RTCDataChannel,
  ): Observable<void> {
    return combineLatest([
      this.initiateRTCPeer(deviceId, peerConnection),
      this.startVideoStreamingAndInteraction(containerEl, peerConnection, dataChannel),
    ]).pipe(
      map(() => void 0),
      finalize(() => ScreenShareUtil.finallyForRTCPeer(peerConnection, dataChannel)),
      takeUntil(this.stopSignalRTC(peerConnection, dataChannel)),
    );
  }

  private onDeviceFrameResolutionUnSafe(dataChannel: RTCDataChannel): Observable<{
    deviceFrameWidth: number;
    deviceFrameHeight: number;
  }> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_FRAME_RESOLUTION_START' })),
      mergeMap(() => this.onRtcDataChannelMessageUnSafe(dataChannel)),
      filter(isNonNullable),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_FRAME_RESOLUTION_SUCCESS' })),
      take(1),
    );
  }

  private onRemoteDescriptionSet(peerConnection: RTCPeerConnection): Observable<void> {
    return timer(0, 100).pipe(
      map(() => peerConnection.remoteDescription),
      filter(isNonNullable),
      take(1),
      map(() => void 0),
    );
  }

  private onRtcDataChannelMessageUnSafe(dataChannel: RTCDataChannel): Observable<{
    deviceFrameWidth: number;
    deviceFrameHeight: number;
  } | null> {
    return fromEvent<MessageEvent>(dataChannel, 'message').pipe(
      tap((event) => this.eventSubject.next({ type: 'INFO', code: 'RTC_DATA_CHANNEL_MESSAGE', payload: event })),
      map((event) => this.whenRtcDataChannelMessage(event)),
    );
  }

  private openScreenShareSession(deviceId: string): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'OPEN_SESSION_START' })),
      mergeMap(() => this.apiSdkClient.request(createOpenScreenShareSessionV2(deviceId, {}))),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'OPEN_SESSION_SUCCESS' })),
      catchError((error) => this.rethrowError('OPEN_SESSION_ERROR', error)),
    );
  }

  private parseConfiguration(configuration: string): Observable<Record<string, unknown>> {
    try {
      return of(ScreenShareUtil.parseJson(configuration)) as Observable<Record<string, unknown>>;
    } catch (error) {
      return this.rethrowError('GET_CONFIGURATION_ERROR_NOT_ALLOWED_BY_USER', error);
    }
  }

  private parseDeviceAnswer(answer: string): RTCLocalSessionDescriptionInit | null {
    try {
      return ScreenShareUtil.parseJson(answer) as RTCLocalSessionDescriptionInit;
    } catch (error) {
      this.eventSubject.next({ type: 'ERROR', code: 'PARSE_DEVICE_ANSWER', payload: error });
      return null;
    }
  }

  private parseDeviceICECandidate(ice: string): Observable<RTCIceCandidate[]> {
    try {
      const { candidates } = ScreenShareUtil.parseJson(ice);
      return Array.isArray(candidates)
        ? of(candidates as RTCIceCandidate[])
        : this.rethrowError('DEVICE_ICE_CANDIDATE_MALFORMED', candidates);
    } catch (error) {
      return this.rethrowError('DEVICE_ICE_CANDIDATE_UN_PARSABLE', error);
    }
  }

  private parseDeviceOffer(offer: string): RTCSessionDescriptionInit | null {
    try {
      return ScreenShareUtil.parseJson(offer) as RTCSessionDescriptionInit;
    } catch (error) {
      this.eventSubject.next({ type: 'ERROR', code: 'PARSE_DEVICE_OFFER', payload: error });
      return null;
    }
  }

  private registerBrowserAnswer(
    peerConnection: RTCPeerConnection,
    browserAnswer: RTCLocalSessionDescriptionInit,
  ): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'BROWSER_ANSWER_REGISTRATION_START' })),
      mergeMap(() => from(peerConnection.setLocalDescription(browserAnswer))),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'BROWSER_ANSWER_REGISTRATION_SUCCESS' })),
      catchError((error) => this.rethrowError('BROWSER_ANSWER_REGISTRATION_ERROR', error)),
    );
  }

  private registerDeviceOffer(
    peerConnection: RTCPeerConnection,
    deviceOffer: RTCSessionDescriptionInit,
  ): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'DEVICE_OFFER_REGISTRATION_START' })),
      mergeMap(() => from(peerConnection.setRemoteDescription(deviceOffer))),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'DEVICE_OFFER_REGISTRATION_SUCCESS' })),
      catchError((error) => this.rethrowError('DEVICE_OFFER_REGISTRATION_ERROR', error)),
    );
  }

  private rethrowError(code: ScreenShareEvent['code'], error?: unknown): Observable<never> {
    this.eventSubject.next({ type: 'ERROR', code });
    this.sessionStatusSubject.next(ScreenShareSessionStatus.ERROR);
    return throwError(() => ({ code, error }));
  }

  private sendAndRegisterBrowserAnswer(
    deviceId: string,
    browserAnswer: RTCSessionDescriptionInit,
    peerConnection: RTCPeerConnection,
  ): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() => this.sendAnswer(deviceId, browserAnswer)),
      mergeMap(() => this.waitForDeviceAcknowledgement(deviceId)),
      mergeMap(() => this.registerBrowserAnswer(peerConnection, browserAnswer)), // Safely wait for the device to do the steRemoteDescription before sending browser ICE (Browser ICE start when localDescription is set)
    );
  }

  private sendAnswer(deviceId: string, browserAnswer: RTCSessionDescriptionInit): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'SEND_ANSWER_START' })),
      map(() => ScreenShareUtil.transformAnswerToString(browserAnswer)),
      mergeMap((answer) => this.apiSdkClient.request(createScreenShareBrowserAnswerV2(deviceId, { answer }))),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'SEND_ANSWER_SUCCESS' })),
      catchError((error) => this.rethrowError('SEND_ANSWER_ERROR', error)),
    );
  }

  private sendBrowserICECandidates(deviceId: string, candidates: RTCIceCandidate[]): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'SEND_BROWSER_ICE_CANDIDATE_START' })),
      mergeMap(() => this.waitForScreenShareStatus(deviceId, [enumScreenShareStatus.SIGNALING])),
      map(() => JSON.stringify({ candidates })),
      mergeMap((payload) => this.apiSdkClient.request(createScreenShareBrowserIceV2(deviceId, { ice: payload }))),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'SEND_BROWSER_ICE_CANDIDATE_SUCCESS' })),
      catchError((error) => this.rethrowError('SEND_BROWSER_ICE_CANDIDATE_ERROR', error)),
    );
  }

  private setupRemoteDesktopCanvasRx(
    containerEl: HTMLElement,
    remoteStream: MediaStream,
    remoteSize: RemoteFrameSize,
  ): Observable<[HTMLCanvasElement, DeviceFrameRatio]> {
    this.eventSubject.next({ type: 'INFO', code: 'SETUP_REMOTE_DESKTOP_CANVAS_RX_START' });
    const { video, canvas, ctx } = ScreenShareUtil.initiateCanvasAndVideo(remoteStream, containerEl);
    const { width, height } = ScreenShareUtil.computeContainedSize(containerEl, remoteSize);
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.style.borderRadius = `8px`;
    const horizontalRatio = remoteSize.deviceFrameWidth / width;
    const verticalRatio = remoteSize.deviceFrameHeight / height;
    this.eventSubject.next({ type: 'INFO', code: 'SETUP_REMOTE_DESKTOP_CANVAS_RX_SUCCESS' });
    return new Observable((subscriber) => {
      const frameObs = of(void 0).pipe(
        mergeMap(() => ScreenShareUtil.onVideoHaveMetadataSafe(video)),
        mergeMap(() => from(video.play())),
        mergeMap(() => animationFrames()),
        tap(() => {
          if (!video.paused && !video.ended) {
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
          }
        }),
      );
      subscriber.next([canvas, { horizontalRatio, verticalRatio }]);
      subscriber.add(frameObs.subscribe());
      return () => {
        subscriber.unsubscribe();
        video.pause();
        video.srcObject = null;
        video.remove();
        canvas.remove();
      };
    });
  }

  private startInteractionCapture(
    dataChannel: RTCDataChannel,
    canvas: HTMLCanvasElement,
    deviceFrameRatio: DeviceFrameRatio,
  ): Observable<void> {
    return of(void 0).pipe(
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'START_CAPTURE_GESTURE' })),
      mergeMap(() =>
        merge(
          ScreenShareUtil.listenGesture(canvas, dataChannel, deviceFrameRatio),
          this.listenButtonTrigger(dataChannel),
        ),
      ),
      mergeMap(() => EMPTY),
      startWith(void 0),
      map(() => void 0),
    );
  }

  private startVideoStreamingAndInteraction(
    containerEl: HTMLElement,
    peerConnection: RTCPeerConnection,
    dataChannel: RTCDataChannel,
  ): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() =>
        combineLatest([this.onDeviceFrameResolutionUnSafe(dataChannel), this.getRemoteStream(peerConnection)]),
      ),
      mergeMap(([remoteSize, remoteStream]) => this.setupRemoteDesktopCanvasRx(containerEl, remoteStream, remoteSize)),
      mergeMap(([canvas, deviceFrameRatio]) => this.startInteractionCapture(dataChannel, canvas, deviceFrameRatio)),
    );
  }

  private stopSignalGlobal(): Observable<void> {
    return merge(ScreenShareUtil.onWindowProblemUnsafe(), this.closeSubject).pipe(map(() => void 0));
  }

  private stopSignalRTC(peerConnection: RTCPeerConnection, dataChannel: RTCDataChannel): Observable<void> {
    return merge(
      ScreenShareUtil.onRtcDataChannelProblemSafe(dataChannel),
      ScreenShareUtil.onRtcConnectionProblemSafe(peerConnection),
      ScreenShareUtil.onRtcTrackProblemSafe(peerConnection),
    ).pipe(
      take(1),
      map(() => void 0),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'STOP_RTC_SIGNAL_TRIGGER' })),
    );
  }

  private waitForAllDeviceICECandidates(deviceId: string): Observable<RTCIceCandidate[]> {
    return of(void 0)
      .pipe(
        mergeMap(() =>
          this.errorAfter(
            ScreenShareService.WAIT_DEVICE_ICE_CANDIDATE_TIMEOUT,
            'GET_DEVICE_ICE_CANDIDATE_ERROR_TIMEOUT',
          ),
        ),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_ICE_CANDIDATE_START' })),
        mergeMap(() => timer(0, ScreenShareService.SCREEN_SHARE_DEVICE_ICE_CANDIDATE_INTERVAL)),
        exhaustMap(() => this.getScreenShareDeviceIce(deviceId)),
        filter((response) => !!response?.ice),
        mergeMap(({ ice }) => this.parseDeviceICECandidate(ice)),
        filter((candidates) => candidates.length > 0),
      )
      .pipe(
        take(1),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_ICE_CANDIDATE_SUCCESS' })),
        catchError((error) => this.rethrowError('GET_DEVICE_ICE_CANDIDATE_ERROR', error)),
      );
  }

  private waitForDeviceAcknowledgement(deviceId: string): Observable<void> {
    return of(void 0)
      .pipe(
        mergeMap(() =>
          this.errorAfter(ScreenShareService.WAIT_DEVICE_ANSWER_TIMEOUT, 'GET_DEVICE_ANSWER_ERROR_TIMEOUT'),
        ),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_ANSWER_START' })),
        mergeMap(() => timer(0, ScreenShareService.SCREEN_SHARE_DEVICE_ANSWER_INTERVAL)),
        exhaustMap(() => this.getScreenShareDeviceAnswer(deviceId)),
      )
      .pipe(
        filter((response) => !!response?.answer),
        map(({ answer }) => this.parseDeviceAnswer(answer)),
        filter((answer) => isNonNullable(answer)),
        take(1),
        map(() => void 0),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_ANSWER_SUCCESS' })),
        catchError((error) => this.rethrowError('GET_DEVICE_ANSWER_ERROR', error)),
      );
  }

  private waitForDeviceOffer(deviceId: string): Observable<RTCSessionDescriptionInit> {
    return of(void 0)
      .pipe(
        mergeMap(() => this.errorAfter(ScreenShareService.WAIT_DEVICE_OFFER_TIMEOUT, 'GET_DEVICE_OFFER_ERROR_TIMEOUT')),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_OFFER_START' })),
        mergeMap(() => timer(0, ScreenShareService.SCREEN_SHARE_DEVICE_OFFER_INTERVAL)),
        exhaustMap(() => this.getScreenShareDeviceOffer(deviceId)),
      )
      .pipe(
        filter((response) => !!response?.offer),
        map(({ offer }) => this.parseDeviceOffer(offer)),
        filter((offer) => isNonNullable(offer)),
        take(1),
        tap(() => this.eventSubject.next({ type: 'INFO', code: 'GET_DEVICE_OFFER_SUCCESS' })),
        catchError((error) => this.rethrowError('GET_DEVICE_OFFER_ERROR', error)),
      );
  }

  private emmitConnectionRefusedByUser(): Observable<void> {
    this.sessionStatusSubject.next(ScreenShareSessionStatus.RefusedByCustomer);
    this.eventSubject.next({ type: 'INFO', code: 'CONNECTION_REFUSED_BY_CUSTOMER' });
    this.eventSubject.next({ type: 'RESET', code: 'CONNECTION_REFUSED_BY_CUSTOMER' });
    this.connectionRefusedByUser.next();
    return of(void 0);
  }

  private manageConnectionRefusedByUser(receivedScreenShareStatus: EnumScreenShareStatusApi): Observable<void> {
    return of(void 0).pipe(
      switchMap(() =>
        receivedScreenShareStatus === enumScreenShareStatus.REFUSED_BY_CUSTOMER
          ? this.emmitConnectionRefusedByUser()
          : of(void 0),
      ),
    );
  }

  private waitForScreenShareStatus(
    deviceId: string,
    enumScreenShareStatusApis: EnumScreenShareStatusApi[],
  ): Observable<void> {
    return of(void 0).pipe(
      mergeMap(() => this.errorAfter(ScreenShareService.WAIT_FOR_STATUS_TIMEOUT, 'READY_FOR_STATUS_TIMEOUT')),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'WAIT_FOR_READY_STATUS_STATUS' })),
      mergeMap(() => timer(0, ScreenShareService.SCREEN_SHARE_STATUS_CHECK_INTERVAL)),
      exhaustMap(() => this.getScreenShareStatus(deviceId)),
      filter(({ status }) => enumScreenShareStatusApis.includes(status)),
      take(1),
      exhaustMap(({ status }) => this.manageConnectionRefusedByUser(status)),
      tap(() => this.eventSubject.next({ type: 'INFO', code: 'READY_FOR_STATUS', payload: enumScreenShareStatusApis })),
      catchError((error) => this.rethrowError('WAIT_FOR_READY_STATUS_STATUS_ERROR', error)),
    );
  }

  private whenRtcDataChannelMessage(messageEvent: MessageEvent): {
    deviceFrameWidth: number;
    deviceFrameHeight: number;
  } | null {
    const message = String(messageEvent.data);
    if (message.startsWith('R')) {
      const wh = message.split('-');
      return {
        deviceFrameWidth: Number.parseInt(wh[1] ?? '100', 10),
        deviceFrameHeight: Number.parseInt(wh[2] ?? '100', 10),
      };
    } else if (message.startsWith('D')) {
      if (isScreenShareRefusedByUserMessage(message)) {
        this.eventSubject.next({ type: 'INFO', code: 'SCREEN_SHARE_SESSION_REFUSED_BY_USER' });
        this.sessionStatusSubject.next(ScreenShareSessionStatus.RefusedByCustomer);
      } else {
        this.eventSubject.next({ type: 'INFO', code: 'SCREEN_SHARE_SESSION_TIMEOUT' });
      }
      this.closeScreenShare();
      return null;
    } else {
      this.eventSubject.next({ type: 'INFO', code: 'UNEXPECTED_MESSAGE_FROM_DEVICE', payload: message });
      return null;
    }
  }
}

export function isScreenShareRefusedByUserMessage(message: string): boolean {
  const [, ...reasonParts] = message.split('-');
  return reasonParts.join('-').toLowerCase().includes('refused by user');
}
